'use client';

import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { useEffect, useContext, useState, type FC, ReactNode } from 'react';
import { Flex, Text, Toggle, Heading } from '@/_components';
import type { TwoFactorCardProps } from './TwoFactorCard.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { formatPhoneNumber } from '@/_utils/phoneNumbers';
import { updateUser } from '@/_api/users';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';

export const TwoFactorCard: FC<TwoFactorCardProps> = () => {
  const { isAdvisor, token, user, setUser } = useContext(UserContext);
  const { previewUser } = useContext(UserPreviewContext);
  const { preferences } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);

  const [isChecked, setChecked] = useState(false);

  useEffect(() => {
    if (user?.enabled_2fa) setChecked(!!user?.enabled_2fa);
  }, [user?.enabled_2fa]);

  const onToggle = async (event: React.FormEvent<any>) => {
    event?.preventDefault?.();
    if (!user?.id) return;

    const newValue: boolean = !isChecked;

    try {
      const { error } = await updateUser({
        token,
        userId: user?.id,
        enabled_2fa: newValue,
      });

      if (!error) {
        addToast('success', 'Profile updated successfully');
        setUser({ ...user, enabled_2fa: newValue });
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setChecked(newValue);
    }
  };

  const isForceChecked: boolean = Boolean(
    preferences?.force_2fa_on_users || preferences?.force_2fa_on_firm_users,
  );
  const isDisabled: boolean = Boolean(
    isForceChecked ||
      (!user?.phone_number && user?.preferred_sign_in_method !== 'password'),
  );

  const descriptionVia: ReactNode = (
    <b>
      via{' '}
      {user?.preferred_sign_in_method === 'password' ||
      user?.preferred_sign_in_method === 'sms'
        ? user?.email || 'email'
        : formatPhoneNumber(user?.phone_number) || 'text'}
    </b>
  );

  const description: ReactNode = !user?.phone_number ? (
    <>Add your phone number to setup two-factor authentication</>
  ) : preferences?.force_2fa_on_users ? (
    <>
      Two-factor authentication is turned on for all users of your firm. You
      will receive a code {descriptionVia} prior to each login attempt.
    </>
  ) : (
    <>
      When you activate two-factor authentication, you will receive a code{' '}
      {descriptionVia} prior to each login attempt.
    </>
  );

  const isPreview: boolean =
    isAdvisor && !!previewUser?.id && previewUser?.id !== user?.id;

  return (
    <Flex isColumn={true} gap={6}>
      <Flex isBetween={true} gap={12}>
        <Flex isColumn={true} gap={2}>
          <Heading level={6}>Two-Factor Authentication</Heading>
          <Text type="tiny" color="secondary">
            {description}
          </Text>
        </Flex>
        <Toggle
          isLabelHidden={true}
          label="Two-Factor Authentication"
          isDisabled={isDisabled || isPreview}
          isChecked={isChecked || isForceChecked}
          onChange={onToggle}
          helperText={
            user?.preferred_sign_in_method !== 'password'
              ? '2FA is disabled until you add another sign-in method'
              : undefined
          }
        />
      </Flex>
    </Flex>
  );
};
